// The forecast chart: cumulative impressions, reported days as a solid line,
// the projection as a dashed line inside its p10–p90 band, today marked.
//
// Hand-rolled SVG like every chart in this app (`performance-trend-chart.tsx`
// is the pattern); no library. The pure `forecastChartModel` does the scale
// and path arithmetic so it can be tested without a DOM, and the component
// is markup on top of it.

import type { CampaignForecastPoint } from '@/api/manage-types';
import { fmtDayShort } from '@/lib/datetime';

export interface ForecastChartModel {
  width: number;
  height: number;
  /** The reported cumulative line, as an SVG points string. */
  actualPoints: string;
  /** The projected cumulative line from the last reported day on. */
  projectedPoints: string;
  /** The band polygon between low and high, closed. */
  bandPoints: string;
  /** Horizontal gridlines with their labels. */
  gridlines: { y: number; label: string }[];
  /** The x of the last reported day, or null for a draft. */
  todayX: number | null;
  /** Axis labels: first day, today, last day. */
  labels: { x: number; text: string; anchor: 'start' | 'middle' | 'end' }[];
  /** Where flights meet, as x positions. */
  flightBoundaries: number[];
}

const PAD = { left: 8, right: 38, top: 10, bottom: 16 };

/**
 * The y scale: three to five gridlines on a 1 / 2 / 2.5 / 5 step, the top
 * one the lowest that still clears the peak — so a 1.0M projection is drawn
 * against 1.25M, not against a 2M ceiling that halves the chart.
 */
function niceScale(peak: number): { top: number; step: number } {
  if (peak <= 0) return { top: 1, step: 0.25 };
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  let best: { top: number; step: number } | null = null;
  for (const exponent of [magnitude / 10, magnitude, magnitude * 10]) {
    for (const unit of [1, 2, 2.5, 5]) {
      const step = unit * exponent;
      const ticks = Math.ceil(peak / step - 1e-9);
      if (ticks < 3 || ticks > 5) continue;
      const top = ticks * step;
      if (!best || top < best.top - 1e-9) best = { top, step };
    }
  }
  return best ?? { top: peak, step: peak / 4 };
}

const trimZeros = (text: string) => text.replace(/\.0+$|(\.\d*?)0+$/, '$1');

/** Gridline labels keep the precision the step needs: 1.25M, 750K, 2.5K, 0.25.
 *  A sub-one step is a near-empty projection, and rounding it labelled every
 *  gridline "1". */
export function tickLabel(value: number): string {
  if (value >= 1_000_000) return `${trimZeros((value / 1_000_000).toFixed(2))}M`;
  if (value >= 1_000) return `${trimZeros((value / 1_000).toFixed(1))}K`;
  if (value < 10) return trimZeros(value.toFixed(2));
  return `${Math.round(value)}`;
}

export function compactCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return `${Math.round(value)}`;
}

export function forecastChartModel(
  series: CampaignForecastPoint[],
  options: { width?: number; height?: number; flightStarts?: string[]; today?: string } = {},
): ForecastChartModel | null {
  const width = options.width ?? 308;
  const height = options.height ?? 126;
  if (series.length < 2) return null;
  const plotWidth = width - PAD.left - PAD.right;
  const plotHeight = height - PAD.top - PAD.bottom;
  const peak = Math.max(...series.map((point) => Math.max(point.high_impressions, point.projected_impressions)), 1);
  const { top, step } = niceScale(peak);
  const x = (index: number) => PAD.left + (plotWidth * index) / (series.length - 1);
  const y = (value: number) => PAD.top + plotHeight - (plotHeight * Math.min(value, top)) / top;
  const lastActual = series.reduce((found, point, index) => (point.actual_impressions !== undefined && point.actual_impressions !== null ? index : found), -1);
  const actual = series
    .slice(0, lastActual + 1)
    .map((point, index) => `${x(index).toFixed(1)},${y(point.actual_impressions ?? 0).toFixed(1)}`)
    .join(' ');
  const projectedFrom = Math.max(lastActual, 0);
  const projected = series
    .slice(projectedFrom)
    .map((point, offset) => `${x(projectedFrom + offset).toFixed(1)},${y(point.projected_impressions).toFixed(1)}`)
    .join(' ');
  const upper = series
    .slice(projectedFrom)
    .map((point, offset) => `${x(projectedFrom + offset).toFixed(1)},${y(point.high_impressions).toFixed(1)}`);
  const lower = series
    .slice(projectedFrom)
    .map((point, offset) => `${x(projectedFrom + offset).toFixed(1)},${y(point.low_impressions).toFixed(1)}`)
    .reverse();
  const gridlines = Array.from({ length: Math.round(top / step) }, (_, index) => {
    const value = step * (index + 1);
    return { y: Number(y(value).toFixed(1)), label: tickLabel(value) };
  });
  const first = series[0]!;
  const last = series[series.length - 1]!;
  const labels: ForecastChartModel['labels'] = [
    { x: PAD.left, text: fmtDayShort(first.day).toUpperCase(), anchor: 'start' },
    { x: PAD.left + plotWidth, text: fmtDayShort(last.day).toUpperCase(), anchor: 'end' },
  ];
  // TODAY is the calendar day, not the last reported one: reporting lags the
  // provider by a day or more, and marking the last report as today read a
  // Sep 7 cutoff as "today" on Sep 18 (audit, 2026-09-18). Without a date the
  // marker falls back to the last reported point, which is all a caller
  // without the projection's own `today` can say.
  let todayAt: number | null = null;
  if (options.today) {
    const index = series.findIndex((point) => point.day === options.today);
    if (index >= 1) todayAt = index;
    else if (index < 0 && options.today > last.day) todayAt = series.length - 1;
  } else if (lastActual >= 0 && lastActual < series.length - 1) {
    todayAt = lastActual;
  }
  const todayX = todayAt !== null ? Number(x(todayAt).toFixed(1)) : null;
  if (todayX !== null && todayAt !== null && todayAt > 2 && todayAt < series.length - 3) {
    labels.splice(1, 0, { x: todayX, text: 'TODAY', anchor: 'middle' });
  }
  const flightBoundaries = (options.flightStarts ?? [])
    .map((day) => series.findIndex((point) => point.day === day))
    .filter((index) => index > 0)
    .map((index) => Number(x(index).toFixed(1)));
  return {
    width,
    height,
    actualPoints: actual,
    projectedPoints: projected,
    bandPoints: [...upper, ...lower].join(' '),
    gridlines,
    todayX,
    labels,
    flightBoundaries,
  };
}

export function ForecastChart({
  series,
  flightStarts,
  today,
  label,
}: {
  series: CampaignForecastPoint[];
  flightStarts?: string[];
  /** The projection's own calendar day, `YYYY-MM-DD`; where the TODAY rule is drawn. */
  today?: string;
  label: string;
}) {
  const model = forecastChartModel(series, { flightStarts, today });
  if (!model) return null;
  const baseline = model.height - PAD.bottom;
  return (
    <svg
      className="forecast-chart"
      width={model.width}
      height={model.height}
      viewBox={`0 0 ${model.width} ${model.height}`}
      role="img"
      aria-label={label}
    >
      {model.gridlines.map((line) => (
        <g key={line.y}>
          <line x1={PAD.left} y1={line.y} x2={model.width - PAD.right} y2={line.y} className="forecast-chart__grid" />
          <text x={model.width - PAD.right + 4} y={line.y + 3} className="forecast-chart__tick">{line.label}</text>
        </g>
      ))}
      <line x1={PAD.left} y1={baseline} x2={model.width - PAD.right} y2={baseline} className="forecast-chart__axis" />
      {model.flightBoundaries.map((x) => (
        <line key={x} x1={x} y1={PAD.top} x2={x} y2={baseline} className="forecast-chart__flight" />
      ))}
      {model.bandPoints && <polygon points={model.bandPoints} className="forecast-chart__band" />}
      {model.projectedPoints && <polyline points={model.projectedPoints} className="forecast-chart__projected" />}
      {model.actualPoints && <polyline points={model.actualPoints} className="forecast-chart__actual" />}
      {model.todayX !== null && (
        <line x1={model.todayX} y1={PAD.top} x2={model.todayX} y2={baseline} className="forecast-chart__today" />
      )}
      {model.labels.map((item) => (
        <text key={`${item.x}-${item.text}`} x={item.x} y={model.height - 4} textAnchor={item.anchor} className="forecast-chart__tick">
          {item.text}
        </text>
      ))}
    </svg>
  );
}
